/**
 * @typedef {{x: number, y: number}} Coordinate
 */
/**
 * @typedef {{x: number, y: number, width: number, height: number}} Rectangle
 */

/**
 *  JavaScript % (modulo) gives a negative result for negative numbers
 *  The function is used to emulate a toroidal array by mapping elements below index 0 or beyond
 *  Eg. -1  % 8  //  -1, not 7
 *
 * @param {number} n
 * @param {number} m
 *
 */
export const negativeNumbersModulo = function(n, m) {
  return ((n % m) + m) % m
}

/**
 * Converts the given amount of degrees to radians.
 *
 * @param {number} angle
 * @returns {number}
 */
export const degreesToRadians = function(angle) {
  return angle * (Math.PI / 180)
}

/**
 * Converts the given amount of radians to degrees.
 * @param {number} angle
 * @return {number}
 */
export const radiansToDegrees = function(angle) {
  return (angle * 180) / Math.PI
}

/**
 * Returns the angle between two coordinates in degrees.
 *
 * @param {Coordinate} coordinateA
 * @param {Coordinate} coordinateB
 * @return {number}
 */
export const getAngleBetweenCoordinates = (coordinateA, coordinateB) => {
  return radiansToDegrees(
    Math.atan2(coordinateA.y - coordinateB.y, coordinateA.x - coordinateB.x)
  )
}

/**
 * Returns the distance between two coordinates.
 *
 * @param {Coordinate} coordinateA
 * @param {Coordinate} coordinateB
 * @return {number}
 */
export const getDistanceBetweenCoordinates = (coordinateA, coordinateB) => {
  return Math.sqrt(
    Math.pow(coordinateB.x - coordinateA.x, 2) +
      Math.pow(coordinateB.y - coordinateA.y, 2)
  )
}

/**
 * Returns the vertical distance between two coordinates.
 *
 * @param {Coordinate} coordinateA
 * @param {Coordinate} coordinateB
 * @return {number}
 */
export const getVerticalDistanceBetweenCoordinates = (
  coordinateA,
  coordinateB
) => {
  return Math.abs(coordinateB.y - coordinateA.y)
}

/**
 * Returns the horizontal distance between two coordinates.
 *
 * @param {Coordinate} coordinateA
 * @param {Coordinate} coordinateB
 * @return {number}
 */
export const getHorizontalDistanceBetweenCoordinates = (
  coordinateA,
  coordinateB
) => {
  return Math.abs(coordinateB.x - coordinateA.x)
}

/**
 * Returns true if the given coordinate is within the given rectangle.
 *
 * @param {Coordinate} coordinate
 * @param {Rectangle} rectangle
 * @return {boolean}
 */
export const isCoordinateWithinRectangle = (coordinate, rectangle) => {
  return (
    coordinate.x > rectangle.x &&
    coordinate.x < rectangle.x + rectangle.width &&
    coordinate.y > rectangle.y &&
    coordinate.y < rectangle.y + rectangle.height
  )
}

/**
 * Returns the circumference of a circle with the given radius.
 *
 * @param {number} radius
 * @return {number}
 */
export const getCircleCircumference = radius => {
  return 2 * Math.PI * radius
}

/**
 * Returns true if the given value is within the given range.
 *
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @return {boolean}
 */
export const isWithinRange = (value, min, max) => {
  return value >= min && value <= max
}

/**
 * Clamps the given value between min and max, so that the returned value will never exceed the two.
 *
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @return {number}
 */
export const clamp = (value, min, max) => {
  return Math.min(Math.max(value, min), max)
}
